// Slot Booking helpers (Dentist scheduled orders)

export const SLOT_START_HOUR = 9;   // 09:00
export const SLOT_END_HOUR = 19;    // last slot starts before 19:00
export const SLOT_INTERVAL_MIN = 30;

export const SLOT_MAX_DAYS_AHEAD = 60; // kitne din aage tak booking allowed

export const SLOT_TAG = "Scan Pending"; // dentistTag sent by backend for slot orders waiting for scan

// Local date -> "YYYY-MM-DD" (no UTC shift)
export const toYMD = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;

export const getTodayYMD = (): string => toYMD(new Date());

// Accepts "2026-10-05" or "2026-10-05T00:00:00" -> "2026-10-05"
export const normalizeYMD = (value?: string | null): string =>
  value ? String(value).slice(0, 10) : "";

// ["09:00", "09:30", ... "18:30"]
export const buildSlotTimes = (): string[] => {
  const times: string[] = [];
  for (let m = SLOT_START_HOUR * 60; m < SLOT_END_HOUR * 60; m += SLOT_INTERVAL_MIN) {
    const hh = String(Math.floor(m / 60)).padStart(2, "0");
    const mm = String(m % 60).padStart(2, "0");
    times.push(`${hh}:${mm}`);
  }
  return times;
};

// "14:30" -> "2:30 PM"
export const formatSlotTime = (time?: string | null): string => {
  if (!time) return "";
  const [h, m] = time.split(":").map(Number);
  const suffix = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m).padStart(2, "0")} ${suffix}`;
};

// "2026-10-05" -> "05 Oct 2026"
export const formatSlotDate = (ymd?: string | null): string => {
  const v = normalizeYMD(ymd);
  if (!v) return "";
  const [y, m, d] = v.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// Today ka slot ho to nikal chuka time disable karne ke liye
export const isTimePassedToday = (time: string): boolean => {
  const now = new Date();
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m <= now.getHours() * 60 + now.getMinutes();
};

// Scan upload tabhi allowed jab scheduled date aa gayi ho (ya guzar gayi ho)
export const canUploadScanNow = (slotDate?: string | null): boolean => {
  const v = normalizeYMD(slotDate);
  if (!v) return true;
  return v <= getTodayYMD();
};

// "2026-10-05" -> Date (local midnight)
export const parseYMD = (ymd?: string | null): Date | null => {
  const v = normalizeYMD(ymd);
  if (!v) return null;
  const [y, m, d] = v.split("-").map(Number);
  return new Date(y, m - 1, d);
};

export const addDaysYMD = (days: number): string => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return toYMD(d);
};

// "Mon, 05 Oct 2026"
export const formatSlotDateLong = (ymd?: string | null): string => {
  const d = parseYMD(ymd);
  return d
    ? d.toLocaleDateString("en-GB", {
        weekday: "short",
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "";
};

export interface SlotGroup {
  label: string;
  icon: string; // bootstrap-icons class
  times: string[];
}

// Time slots ko Morning / Afternoon / Evening me baantna
export const buildSlotGroups = (): SlotGroup[] => {
  const all = buildSlotTimes();
  const hour = (t: string) => Number(t.split(":")[0]);
  return [
    { label: "Morning", icon: "bi-sunrise", times: all.filter((t) => hour(t) < 12) },
    { label: "Afternoon", icon: "bi-sun", times: all.filter((t) => hour(t) >= 12 && hour(t) < 16) },
    { label: "Evening", icon: "bi-moon-stars", times: all.filter((t) => hour(t) >= 16) },
  ].filter((g) => g.times.length > 0);
};
